import { AppHeader } from '@/components/AppHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { Shield, Sparkles, X, ArrowLeft, Users, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SEO } from '@/components/SEO';

export default function PrivacyPolicy() {
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user ?? null);
    });
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Privacy Policy - Therappi®"
        description="How Therappi® collects, uses and protects your personal and health data. GDPR-aligned and built with clinical-grade privacy."
        path="/privacy"
      />
      <AppHeader user={user} onSignOut={handleSignOut} />
      
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Card>
          <CardHeader className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="absolute right-4 top-4 h-8 w-8 rounded-full"
            >
              <X className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2 mb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(-1)}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            </div>
            <CardTitle className="flex items-center gap-2 text-3xl">
              <Shield className="h-8 w-8" />
              Privacy Policy
            </CardTitle>
            <p className="text-muted-foreground">Last updated: January 2026</p>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none dark:prose-invert">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
              <p>
                Your privacy is critically important to us. This Privacy Policy explains how Therappi<sup>®</sup> ("we", "us", "our") collects, uses, discloses, and safeguards your information when you use our therapeutic wellness application.
              </p>
              <p className="mt-2">
                <strong>Data Controller:</strong> Therappi Ltd, Thames House, 77-77a High Street, Esher, Surrey, KT10 9PA, United Kingdom
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold mb-3">2.1 Health & Wellness Data</h3>
              <p>We collect and process the following health-related information:</p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>AI Therappi<sup>®</sup> Chat Data:</strong> Conversation content, sentiment analysis, crisis indicators</li>
                <li><strong>Daily Check-ins:</strong> Mood ratings, energy levels, sleep quality, stress levels, alcohol consumption</li>
                <li><strong>Nutrition Data:</strong> Food logs, recipes, nutritional analysis, gut health scores</li>
                <li><strong>Sleep Data:</strong> Sleep duration, quality ratings, dream journals</li>
                <li><strong>Activity Data:</strong> Exercise logs, wellness activities, meditation sessions</li>
                <li><strong>Menstrual & Reproductive Health:</strong> Cycle tracking, fertility data, pregnancy milestones (where applicable)</li>
                <li><strong>Mental Health Tracking:</strong> Emotional symptoms, anxiety/depression indicators, crisis events</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">2.2 Inflammatory Load (IL) Scoring System</h3>
              <p>
                Our <strong>Inflammatory Load Intelligence System</strong> is an educational wellness tool that analyses your lifestyle patterns to provide personalised insights. 
                <strong className="block mt-2">Important: The IL Score is NOT a medical test or diagnosis.</strong>
              </p>
              <p className="mt-2">The IL system collects and processes:</p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Dietary patterns:</strong> Ultra-processed food intake, sugar consumption, fibre intake, omega-3 consumption</li>
                <li><strong>Sleep patterns:</strong> Duration, quality ratings, consistency metrics</li>
                <li><strong>Stress levels:</strong> Self-reported daily stress ratings</li>
                <li><strong>Activity data:</strong> Weekly exercise minutes, intensity levels</li>
                <li><strong>Alcohol consumption:</strong> Weekly units and patterns</li>
                <li><strong>HRV data (WHOOP users only):</strong> Heart rate variability metrics when connected</li>
              </ul>
              <p className="bg-card border border-border p-4 rounded-lg text-foreground">
                <strong className="text-primary">Educational Purpose:</strong> The IL score provides lifestyle-based insights to help you understand potential inflammatory patterns in your daily habits. 
                It does NOT measure actual inflammation in your body and should NOT be used for medical decision-making.
              </p>

              <h3 className="text-xl font-semibold mb-3">2.3 Third-Party Integrations</h3>
              <p>When you connect third-party services, we access:</p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>WHOOP:</strong> Recovery scores, HRV, sleep metrics, strain data, workout logs (encrypted with industry-standard encryption)</li>
                <li><strong>Garmin:</strong> Similar fitness and recovery metrics (when supported)</li>
              </ul>
              <p className="text-sm text-muted-foreground">
                These integrations are optional. You control whether to connect these services and can disconnect at any time from Settings → Integrations.
              </p>
            </section>

            <section className="mb-8 bg-primary/5 p-6 rounded-lg border border-primary/20">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Heart className="h-6 w-6 text-primary" />
                3. Partner Linking & Connected Couples
              </h2>
              <p>
                Therappi<sup>®</sup> offers a <strong>Connected Couples</strong> feature allowing you to link your account with a partner, family member, or accountability buddy for shared wellness support.
              </p>
              
              <h3 className="text-xl font-semibold mb-3 mt-4">3.1 Data Collected for Partner Linking</h3>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Email addresses:</strong> Used to send and receive partner invitations</li>
                <li><strong>Link status:</strong> Pending, accepted, or declined invitation status</li>
                <li><strong>Relationship type:</strong> Romantic, family, friendship, or other (as selected by you)</li>
                <li><strong>Display names:</strong> Shared between linked partners for identification</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">3.2 What Data Partners Can See</h3>
              <p>When linked, partners may access:</p>
              <ul className="list-disc pl-6 mb-4">
                <li>Shared exercise templates and completed exercises</li>
                <li>Relationship harmony scores (aggregated, not detailed logs)</li>
                <li>Shared appreciations and relationship reflections</li>
                <li>Partner check-in completion status (not detailed content)</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">
                <strong>Your therapy conversations, crisis plans, detailed health logs, and personal reflections are NEVER shared with partners.</strong>
              </p>

              <h3 className="text-xl font-semibold mb-3">3.3 Unlinking & Data Removal</h3>
              <p>
                Either partner can unlink at any time from Settings. When you unlink:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>The partner link is immediately severed</li>
                <li>Your partner loses access to any shared data</li>
                <li>Shared exercise history remains in your individual accounts</li>
                <li>You can re-link with the same or different partner later</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">4. How We Use Your Information</h2>
              <p>We use your information for:</p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Personalised therapeutic support:</strong> AI-powered insights, pattern identification, recommendations</li>
                <li><strong>Safety monitoring:</strong> Crisis detection, risk assessment, emergency resource provision</li>
                <li><strong>IL Score computation:</strong> Daily calculation of lifestyle-based inflammatory load indicators</li>
                <li><strong>Progress tracking:</strong> Trends, streaks, achievements, wellness dashboards</li>
                <li><strong>Partner features:</strong> Enabling shared exercises and relationship tools when you choose to link</li>
                <li><strong>Service improvement:</strong> Aggregate analytics, feature optimisation, research (anonymised data only)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">5. Data Security</h2>
              <p>We implement robust security measures:</p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Encryption:</strong> All data encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
                <li><strong>Access controls:</strong> Row-level security (RLS) ensures you only access your own data</li>
                <li><strong>Third-party tokens:</strong> WHOOP and other integration tokens stored with authenticated encryption</li>
                <li><strong>Partner data isolation:</strong> Linked partners only see explicitly shared data, never private health logs</li>
                <li><strong>Audit logging:</strong> All sensitive data access is logged for security monitoring</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">6. Your Rights & Controls</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Access your data:</strong> Download all your data via Settings → Privacy</li>
                <li><strong>Delete your data:</strong> Request complete data deletion (account deletion removes all personal data)</li>
                <li><strong>Opt-out of IL scoring:</strong> Disable Inflammatory Load computation via Settings → Preferences → Data Tracking</li>
                <li><strong>Disconnect integrations:</strong> Revoke WHOOP, Garmin, or other third-party access at any time</li>
                <li><strong>Manage partner links:</strong> Send, accept, decline, or sever partner connections at any time</li>
                <li><strong>Export therapy data:</strong> Download your conversation history and session summaries</li>
                <li><strong>Memory Bank management:</strong> Clear AI memory summaries without losing raw conversation data</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">7. Data Retention</h2>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Active accounts:</strong> Data retained while your account is active</li>
                <li><strong>Deleted accounts:</strong> Personal data permanently deleted within 30 days</li>
                <li><strong>Partner link records:</strong> Deleted immediately when unlinking; invitation history retained for 90 days for abuse prevention</li>
                <li><strong>Audit logs:</strong> Retained for 2 years for security purposes</li>
                <li><strong>Anonymised research data:</strong> May be retained indefinitely (cannot be linked back to you)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">8. Lawful Basis for Processing (GDPR)</h2>
              <p>We process your data under the following legal bases:</p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Consent:</strong> You explicitly consent to data processing when creating an account</li>
                <li><strong>Contractual necessity:</strong> Processing required to provide the therapeutic services you requested</li>
                <li><strong>Legitimate interests:</strong> Service improvement, fraud prevention, security monitoring</li>
                <li><strong>Special category data (health data):</strong> Processed under explicit consent (GDPR Article 9)</li>
                <li><strong>Partner linking:</strong> Both parties must consent to link; consent can be withdrawn at any time</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">9. Age Requirements</h2>
              <p>
                Our service requires users to be at least 13 years old. Users aged 13-17 require parental consent.
                Age verification is conducted during registration. Parents/guardians of minor users are responsible for monitoring their usage.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">10. Third-Party Services</h2>
              <p>We integrate with the following third-party providers:</p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>WHOOP:</strong> Fitness and recovery data (subject to WHOOP's privacy policy)</li>
                <li><strong>Anthropic (Claude AI):</strong> AI-powered therapy chat processing</li>
                <li><strong>Stripe:</strong> Payment processing (we do NOT store payment card details)</li>
                <li><strong>Supabase:</strong> Database hosting and authentication</li>
                <li><strong>Resend:</strong> Email notifications (partner invites, password resets)</li>
              </ul>
              <p className="text-sm text-muted-foreground">
                These providers have their own privacy policies. We recommend reviewing them before using integrations.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">11. International Data Transfers</h2>
              <p>
                Your data may be processed in countries outside the UK/EEA. Where this occurs, we ensure appropriate safeguards
                are in place, including Standard Contractual Clauses (SCCs) approved by the European Commission.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">12. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy to reflect changes in our practices or legal requirements. 
                Material changes will be communicated via email or in-app notification.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">13. Contact Us</h2>
              <p>
                For privacy questions, data requests, or concerns, contact us at: <a href="mailto:support@therappi.app" className="text-primary hover:underline">support@therappi.app</a>
              </p>
            </section>

            <section className="bg-card border border-border p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3 flex items-center gap-2 text-primary">
                <Sparkles className="h-5 w-5 text-therapeutic-gold" />
                Your Data, Your Control
              </h3>
              <p>
                We believe in radical transparency and user empowerment. Every piece of data you generate belongs to you. 
                Access your Privacy Dashboard anytime to see exactly what we've collected, download it, or delete it.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
